import { useEffect, useState, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "../../../../utils/axiosInstance";
import { AuthContext } from "../../../../context/AuthContext";
import { isFreelance } from "../../../../utils/constants/accountType";
import {
  ProjectStatusMark,
  statusRail,
} from "../../../../components/Status/ProjectStatus";
import { btn } from "../../../../components/Button/buttonStyles";

// Text columns read left, money reads right.
// Project Name and Client get more space.
// Start Date and Deadline get less space.
// Financial columns get equal, medium-sized widths.
const buildColumns = (freelance) =>
  [
    { label: "Code", align: "left", width: 10 },
    { label: "Project Name", align: "left", width: 15 },
    { label: "Client", align: "left", width: 15 },
    { label: "Start Date", align: "left", width: 10 },
    { label: "Deadline", align: "left", width: 10 },
    !freelance && {
      label: "Primary Manager",
      align: "left",
      width: 15,
    },
    {
      label: "Total to receive, INR",
      align: "right",
      width: 11.67,
    },
    {
      label: "Total to pay, INR",
      align: "right",
      width: 11.67,
    },
    {
      label: "Profit Margin, %",
      align: "right",
      width: 11.66,
    },
  ].filter(Boolean);

const ALIGN = {
  left: "text-left",
  right: "text-right",
};

// Long text is allowed to wrap.
const TEXT_CELL =
  "px-3 py-2 text-xs align-top text-gray-700 break-words";

const TIGHT_CELL =
  "px-3 py-2 text-xs align-top text-gray-700 whitespace-nowrap";

const MONEY_CELL =
  "px-3 py-2 text-xs align-top text-right text-gray-700 whitespace-nowrap";

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const freelance = isFreelance(user);
  const COLUMNS = buildColumns(freelance);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Calculate total column width so the percentages
  // remain correct even when Primary Manager is removed.
  const totalWidth = COLUMNS.reduce(
    (sum, column) => sum + column.width,
    0
  );

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await api.get("/projects", {
          withCredentials: true,
        });

        setProjects(response.data.data);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch projects");
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  const getClientName = (client) => {
    if (!client) return "—";

    if (client.type?.toLowerCase() === "company") {
      return client.company_name || "—";
    }

    if (client.primary_user) {
      return `${client.primary_user.first_name} ${client.primary_user.last_name}`.trim();
    }

    return "—";
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">
          Projects
        </h1>

        <div className="flex items-center gap-2 text-sm">
          <button
            onClick={() => navigate("/projects/create-project")}
            className={btn("primary", "sm")}
          >
            Add Project
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-brand-600" />
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-danger-600 text-sm">
          {error}
        </p>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Item count */}
          <p className="text-sm text-gray-600">
            Showing{" "}
            <span className="font-semibold">
              {projects.length}
            </span>{" "}
            items.
          </p>

          {/* Table */}
          <div className="w-full max-w-full bg-white shadow rounded-lg overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto overflow-x-auto">
              <table className="w-full table-fixed border-collapse">
                {/* Table Header */}
                <thead className="sticky top-0 z-10">
                  <tr className="bg-white border-b text-[10px] tracking-widest text-gray-500 uppercase">
                    {COLUMNS.map((column) => (
                      <th
                        key={column.label}
                        style={{
                          width: `${
                            (column.width / totalWidth) * 100
                          }%`,
                        }}
                        className={`px-3 py-2 font-semibold align-bottom whitespace-nowrap ${
                          ALIGN[column.align]
                        }`}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody>
                  {projects.length === 0 ? (
                    <tr>
                      <td
                        colSpan={COLUMNS.length}
                        className="py-10 text-center text-gray-400 text-xs"
                      >
                        No projects found.
                      </td>
                    </tr>
                  ) : (
                    projects.map((project, index) => {
                      const rowBg =
                        index % 2 === 0
                          ? "bg-gray-50"
                          : "bg-white";

                      return (
                        <tr
                          key={project.id}
                          className={`${rowBg} border-l-4 ${statusRail(
                            project.status
                          )} hover:bg-brand-50 transition-colors`}
                        >
                          {/* Code */}
                          <td className="px-3 py-2 text-xs align-top whitespace-nowrap">
                            <Link
                              to={`/project/${project.id}`}
                              className="text-brand-600 hover:text-brand-800 underline font-medium"
                            >
                              {project.project_code || "—"}
                            </Link>

                            <div className="mt-0.5">
                              <ProjectStatusMark
                                status={project.status}
                              />
                            </div>
                          </td>

                          {/* Project Name */}
                          <td className={TEXT_CELL}>
                            {project.project_name}
                          </td>

                          {/* Client */}
                          <td className={TEXT_CELL}>
                            {project.client?.id ? (
                              <Link
                                to={`/clients/${project.client.id}`}
                                className="text-brand-600 hover:text-brand-800 underline font-medium"
                              >
                                {getClientName(project.client)}
                              </Link>
                            ) : (
                              getClientName(project.client)
                            )}
                          </td>

                          {/* Start Date */}
                          <td className={TIGHT_CELL}>
                            {project.start_at
                              ? formatDate(project.start_at)
                              : "—"}
                          </td>

                          {/* Deadline */}
                          <td className={TIGHT_CELL}>
                            {project.deadline_at
                              ? formatDate(project.deadline_at)
                              : "—"}
                          </td>

                          {/* Primary Manager */}
                          {!freelance && (
                            <td className={TEXT_CELL}>
                              {project.primaryManager
                                ? `${project.primaryManager.first_name} ${project.primaryManager.last_name}`
                                : "—"}
                            </td>
                          )}

                          {/* Total to Receive */}
                          <td className={MONEY_CELL}>
                            —
                          </td>

                          {/* Total to Pay */}
                          <td className={MONEY_CELL}>
                            —
                          </td>

                          {/* Profit Margin */}
                          <td className={MONEY_CELL}>
                            —
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectsPage;